import { defineMessages, useIntl } from "react-intl";
import { type Dispatch, type SetStateAction, useCallback, useState } from "react";

import Button from "common/core/button";
import WorkflowModal from "common/modals/workflow_modal";
import { useMutation } from "util/graphql";
import { pushNotification } from "common/core/notification_center/actions";
import { NOTIFICATION_SUBTYPES, NOTIFICATION_TYPES } from "constants/notifications";
import { Heading } from "common/core/typography";
import type { SettingProfileEnum } from "graphql_globals";
import { captureException } from "util/exception";
import { useActiveOrganization } from "common/account/active_organization";

import Styles from "./index.module.scss";
import DeleteSettingProfile from "./graphql/delete_setting_profile_mutation.graphql";
import BrandProfileQuery, {
  type BrandProfile,
  type BrandProfileVariables,
} from "../../brand/graphql/brand_profiles.query.graphql";

const MESSAGES = defineMessages({
  modalTitle: {
    id: "dc3c0db5-ff95-4bc6-8074-246b6c2c9c59",
    defaultMessage: "Are you sure you want to delete {profileName}?",
  },
  multipleOrgsOnProfile: {
    id: "65446d1c-ec52-42b4-82ce-6e70133aaa2b",
    defaultMessage:
      "There are {profileUsageCount} organizations using this {profileType} profile. ",
  },
  oneOrgOnProfile: {
    id: "c14e8c65-e865-413b-bb54-8e644b00ce06",
    defaultMessage: "There is {profileUsageCount} organization using this {profileType} profile.",
  },
  noOrgsOnProfile: {
    id: "b127a927-5298-4f7d-9eed-58b52448461f",
    defaultMessage: "No organizations are using this profile.",
  },
  ifProfileDeleted: {
    id: "999dafc0-45ee-457f-a95d-2778dca5e254",
    defaultMessage: "If this profile is deleted:",
  },
  orgsOffProfile: {
    id: "d3e034f5-b3b2-42da-80ce-a80e1cbe77d0",
    defaultMessage:
      "The {profileUsageCount} {profileUsageCount, plural, one {organization} other {organizations}} will be off-profile",
  },
  settingsConfigured: {
    id: "3bc172e7-703a-4085-8ed5-e54ac9a01c13",
    defaultMessage:
      "The settings configured by this profile will remain in-place for the {profileUsageCount} {profileUsageCount, plural, one {organization} other {organizations}}, but will no longer be governed by a profile. ",
  },
  applyNewProfile: {
    id: "451b91b8-6c4a-4cfa-ac58-40bc74874393",
    defaultMessage:
      "You can choose to apply a new {profileType} profile to them from Company directory.",
  },
  cancel: {
    id: "3ed7820e-d6af-42bd-bfd0-43c15b9c7b37",
    defaultMessage: "Cancel",
  },
  deleteProfile: {
    id: "bd2fdf51-882f-4a59-8e17-2cd2f3d3fe37",
    defaultMessage: "Delete profile",
  },
  deleteSuccess: {
    id: "1dce8629-db27-4bd4-983d-a128707cc5af",
    defaultMessage: "{profileName} has been deleted.",
  },
  deleteFailure: {
    id: "4411a07d-f490-4e52-b48f-558861c340b5",
    defaultMessage: "Failed to delete profile. If this issue persists please contact support",
  },
});

type DeleteProfileType = {
  id: string;
  profileName: string;
  profileType: SettingProfileEnum;
  usageCount: number;
};

export function DeleteProfileModal({
  profile,
  setShowDeleteModal,
}: {
  profile: DeleteProfileType;
  setShowDeleteModal: Dispatch<SetStateAction<boolean>>;
}) {
  const intl = useIntl();
  const [activeOrgId] = useActiveOrganization();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const deleteSettingProfileMutation = useMutation(DeleteSettingProfile);

  const handleSubmit = useCallback(() => {
    setIsSubmitting(true);
    deleteSettingProfileMutation({
      variables: {
        input: {
          profileId: Number(profile.id),
        },
      },
      update(cacheProxy) {
        const result = cacheProxy.readQuery<BrandProfile, BrandProfileVariables>({
          query: BrandProfileQuery,
          variables: { organizationId: activeOrgId! },
        })!;
        if (result.organization?.__typename !== "Organization") {
          throw new Error(`Expected organization, got ${result.organization?.__typename}.`);
        }
        const { organization } = result;
        const newNode = {
          ...organization,
          settingProfiles: organization.settingProfiles.filter(
            (cacheProfile) => cacheProfile.id !== profile.id,
          ),
        };
        cacheProxy.writeQuery({
          query: BrandProfileQuery,
          variables: { organizationId: activeOrgId! },
          data: { ...result, organization: newNode },
        });
      },
    })
      .then(() => {
        setShowDeleteModal(false);
        pushNotification({
          position: "topCenter",
          type: NOTIFICATION_TYPES.DEFAULT,
          message: intl.formatMessage(MESSAGES.deleteSuccess, { profileName: profile.profileName }),
          subtype: NOTIFICATION_SUBTYPES.SUCCESS,
        });
      })
      .catch((error) => {
        captureException(error);
        pushNotification({
          position: "topCenter",
          type: NOTIFICATION_TYPES.DEFAULT,
          message: intl.formatMessage(MESSAGES.deleteFailure),
          subtype: NOTIFICATION_SUBTYPES.ERROR,
        });
      })
      .finally(() => setIsSubmitting(false));
  }, [profile]);

  let orgsOnProfileMsg;
  if (profile.usageCount > 1) {
    orgsOnProfileMsg = MESSAGES.multipleOrgsOnProfile;
  } else if (profile.usageCount < 1) {
    orgsOnProfileMsg = MESSAGES.noOrgsOnProfile;
  } else {
    orgsOnProfileMsg = MESSAGES.oneOrgOnProfile;
  }

  const profileTypeText = profile.profileType.toLowerCase();

  return (
    <WorkflowModal
      large
      positionTop
      footerSeparator={false}
      title={intl.formatMessage(MESSAGES.modalTitle, {
        profileName: profile.profileName,
      })}
      buttons={[
        <Button
          key="cancel"
          buttonColor="dark"
          variant="tertiary"
          onClick={() => setShowDeleteModal(false)}
          disabled={isSubmitting}
        >
          {intl.formatMessage(MESSAGES.cancel)}
        </Button>,
        <Button
          key="confirm"
          buttonColor="danger"
          variant="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {intl.formatMessage(MESSAGES.deleteProfile)}
        </Button>,
      ]}
    >
      <Heading className={Styles.defaultText} textStyle={"headingSix"} level={"h3"}>
        {intl.formatMessage(orgsOnProfileMsg, {
          profileUsageCount: profile.usageCount,
          profileType: profileTypeText,
        })}
      </Heading>

      {profile.usageCount > 0 && (
        <>
          <Heading className={Styles.profileDeleteDetails} textStyle={"headingSix"} level={"h4"}>
            {intl.formatMessage(MESSAGES.ifProfileDeleted)}
          </Heading>
          <ul className={Styles.appliedProfileList}>
            <li key={1}>
              {intl.formatMessage(MESSAGES.orgsOffProfile, {
                profileUsageCount: profile.usageCount,
              })}
            </li>
            <li key={2}>
              {intl.formatMessage(MESSAGES.settingsConfigured, {
                profileUsageCount: profile.usageCount,
              })}
            </li>
            <li key={3}>
              {intl.formatMessage(MESSAGES.applyNewProfile, { profileType: profileTypeText })}
            </li>
          </ul>
        </>
      )}
    </WorkflowModal>
  );
}
